import { Dispatch } from 'react';
import {
  addTypeToStorageRequest,
  addTypeToStorageRequests,
  archiveStorageRequest,
  arrangeStorageRequestsByDate,
  cancelStorageRequest,
  confirmStorageRequest,
  createStorageRequest,
  getArchivedStorageRequests,
  getStorageRequests,
  setStorageRequestToSeen,
} from 'services/storageRequestService';
import {
  ACCEPT_STORAGE_REQUEST_FAILURE,
  ACCEPT_STORAGE_REQUEST_REQUEST,
  ACCEPT_STORAGE_REQUEST_SUCCESS,
  ADD_STORAGE_REQUEST,
  ARCHIVE_STORAGE_REQUEST_FAILURE,
  ARCHIVE_STORAGE_REQUEST_REQUEST,
  ARCHIVE_STORAGE_REQUEST_SUCCESS,
  CANCEL_STORAGE_REQUEST_FAILURE,
  CANCEL_STORAGE_REQUEST_REQUEST,
  CANCEL_STORAGE_REQUEST_SUCCESS,
  CREATE_STORAGE_REQUEST_FAILURE,
  CREATE_STORAGE_REQUEST_REQUEST,
  CREATE_STORAGE_REQUEST_SUCCESS,
  DECLINE_STORAGE_REQUEST_FAILURE,
  DECLINE_STORAGE_REQUEST_REQUEST,
  DECLINE_STORAGE_REQUEST_SUCCESS,
  GET_ARCHIVED_STORAGE_REQUESTS_FAILURE,
  GET_ARCHIVED_STORAGE_REQUESTS_REQUEST,
  GET_ARCHIVED_STORAGE_REQUESTS_SUCCESS,
  GET_STORAGE_REQUESTS_FAILURE,
  GET_STORAGE_REQUESTS_REQUEST,
  GET_STORAGE_REQUESTS_SUCCESS,
  SET_STORAGE_REQUEST_TO_SEEN_FAILURE,
  SET_STORAGE_REQUEST_TO_SEEN_REQUEST,
  SET_STORAGE_REQUEST_TO_SEEN_SUCCESS,
} from 'state/actionTypes/storageRequestTypes';
import {
  StorageRequestAction,
  StorageRequestUrlPart,
} from 'state/contexts/storageRequestContext/storageRequest.types';
import { StorageRequest, StorageRequestPayload } from 'types/storageRequest';

type GetStorageRequestAction = (
  dispatch: Dispatch<StorageRequestAction>,
) => Promise<void>;
export const getStorageRequestsAction: GetStorageRequestAction = async (dispatch) => {
  dispatch({ type: GET_STORAGE_REQUESTS_REQUEST });
  try {
    const received = await getStorageRequests('receivedStorageRequests');
    const sent = await getStorageRequests('sentStorageRequests');
    const payload = arrangeStorageRequestsByDate([
      ...addTypeToStorageRequests(received, 'received'),
      ...addTypeToStorageRequests(sent, 'sent'),
    ]);

    dispatch({ type: GET_STORAGE_REQUESTS_SUCCESS, payload });
  } catch (err) {
    dispatch({
      type: GET_STORAGE_REQUESTS_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type createStorageRequestRequestsAction = (
  dispatch: Dispatch<StorageRequestAction>,
  storageRequestPayload: StorageRequestPayload,
) => Promise<void>;
export const createStorageRequestAction: createStorageRequestRequestsAction = async (
  dispatch,
  storageRequestPayload,
) => {
  dispatch({ type: CREATE_STORAGE_REQUEST_REQUEST });
  try {
    const payload = await createStorageRequest(storageRequestPayload);
    dispatch({
      type: CREATE_STORAGE_REQUEST_SUCCESS,
      payload: { ...payload, type: 'sent' },
    });
  } catch (err) {
    dispatch({
      type: CREATE_STORAGE_REQUEST_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type archiveStorageRequestAction = (
  dispatch: Dispatch<StorageRequestAction>,
  storageRequestId: string,
  type: StorageRequestUrlPart,
) => Promise<void>;
export const archiveStorageRequestAction: archiveStorageRequestAction = async (
  dispatch,
  storageRequestId,
  type,
) => {
  dispatch({ type: ARCHIVE_STORAGE_REQUEST_REQUEST });
  try {
    const payload = await archiveStorageRequest(storageRequestId, type);
    dispatch({ type: ARCHIVE_STORAGE_REQUEST_SUCCESS, payload });
  } catch (err) {
    dispatch({
      type: ARCHIVE_STORAGE_REQUEST_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type getArchivedStorageRequestsAction = (
  dispatch: Dispatch<StorageRequestAction>,
) => Promise<void>;
export const getArchivedStorageRequestAction: getArchivedStorageRequestsAction = async (
  dispatch,
) => {
  dispatch({ type: GET_ARCHIVED_STORAGE_REQUESTS_REQUEST });
  try {
    const received = await getArchivedStorageRequests('receivedStorageRequests');
    const sent = await getArchivedStorageRequests('sentStorageRequests');
    const payload = arrangeStorageRequestsByDate([
      ...addTypeToStorageRequests(received, 'received'),
      ...addTypeToStorageRequests(sent, 'sent'),
    ]);
    dispatch({
      type: GET_ARCHIVED_STORAGE_REQUESTS_SUCCESS,
      payload,
    });
  } catch (err) {
    dispatch({
      type: GET_ARCHIVED_STORAGE_REQUESTS_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type ConfirmStorageRequestAction = (
  dispatch: Dispatch<StorageRequestAction>,
  storageRequestId: string,
  type: 'decline' | 'accept',
) => Promise<StorageRequest | undefined>;
export const confirmStorageRequestAction: ConfirmStorageRequestAction = async (
  dispatch,
  storageRequestId,
  type,
) => {
  dispatch({
    type:
      type === 'accept'
        ? ACCEPT_STORAGE_REQUEST_REQUEST
        : DECLINE_STORAGE_REQUEST_REQUEST,
  });
  try {
    const payload = await confirmStorageRequest(storageRequestId, type);
    dispatch({
      type:
        type === 'accept'
          ? ACCEPT_STORAGE_REQUEST_SUCCESS
          : DECLINE_STORAGE_REQUEST_SUCCESS,
      payload: addTypeToStorageRequest(payload, 'received'),
    });

    return payload;
  } catch (err) {
    dispatch({
      type:
        type === 'accept'
          ? ACCEPT_STORAGE_REQUEST_FAILURE
          : DECLINE_STORAGE_REQUEST_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type cancelStorageRequestAction = (
  dispatch: Dispatch<StorageRequestAction>,
  storageRequestId: string,
) => Promise<void>;
export const cancelStorageRequestAction: cancelStorageRequestAction = async (
  dispatch,
  storageRequestId,
) => {
  dispatch({ type: CANCEL_STORAGE_REQUEST_REQUEST });
  try {
    const payload = await cancelStorageRequest(storageRequestId);
    dispatch({ type: CANCEL_STORAGE_REQUEST_SUCCESS, payload });
  } catch (err) {
    dispatch({
      type: CANCEL_STORAGE_REQUEST_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type setStorageRequestToSeenAction = (
  dispatch: Dispatch<StorageRequestAction>,
  storageRequestId: string,
) => Promise<StorageRequest | undefined>;
export const setStorageRequestToSeenAction: setStorageRequestToSeenAction = async (
  dispatch,
  storageRequestId,
) => {
  dispatch({ type: SET_STORAGE_REQUEST_TO_SEEN_REQUEST });
  try {
    const payload = await setStorageRequestToSeen(storageRequestId);
    dispatch({ type: SET_STORAGE_REQUEST_TO_SEEN_SUCCESS, payload });

    return payload;
  } catch (err) {
    dispatch({
      type: SET_STORAGE_REQUEST_TO_SEEN_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type AddStorageRequestAction = (
  dispatch: Dispatch<StorageRequestAction>,
  payload: StorageRequest,
) => void;
export const addStorageRequestAction: AddStorageRequestAction = (dispatch, payload) => {
  dispatch({ type: ADD_STORAGE_REQUEST, payload });
};
